import { useEffect, useRef, useState } from 'react'
import { MoreHorizontal, Loader2 } from 'lucide-react'
import { useFolders, useCreateFolder, useUpdateFolder, useDeleteFolder } from '../hooks/useFolders'
import { useCourses, useRemoveFolderFromCourse } from '../hooks/useCourses'
import { extractErrorMessage } from '../api/client'
import { useSwipeNavBlock } from '../hooks/useSwipeNavBlock'
import GroupIntoCourseSheet from './GroupIntoCourseSheet'
import type { Folder } from '../types'

interface Props {
  onClose: () => void
}

type CourseAction =
  | { type: 'add'; folderId: string; folderTitle: string }
  | { type: 'remove'; folderId: string; folderTitle: string }

export default function FolderManagementModal({ onClose }: Props) {
  const { data } = useFolders()
  const createFolder = useCreateFolder()
  const updateFolder = useUpdateFolder()
  const deleteFolder = useDeleteFolder()
  // ADR-106 P3: mounted only while open, so it blocks for its whole lifetime —
  // including while its own ··· menus or the group-into-course sheet are open.
  useSwipeNavBlock(true, 'folder-management-modal')

  const [newTitle, setNewTitle] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  // ── Course action state ───────────────────────────────────────────────────────
  // Which folder's ··· menu is open (by folder id)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  // Which course action sheet/modal is active
  const [courseAction, setCourseAction] = useState<CourseAction | null>(null)

  const folders = data?.items ?? []

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const title = newTitle.trim()
    if (!title) return
    createFolder.mutate(title, { onSuccess: () => setNewTitle('') })
  }

  function startEdit(folder: Folder) {
    setEditingId(folder.id)
    setEditingTitle(folder.title)
  }

  function handleUpdate(e: React.FormEvent) {
    e.preventDefault()
    if (!editingId) return
    const title = editingTitle.trim()
    if (!title) return
    updateFolder.mutate({ id: editingId, title }, { onSuccess: () => setEditingId(null) })
  }

  function handleDelete(id: string) {
    deleteFolder.mutate(id, { onSuccess: () => setConfirmDeleteId(null) })
  }

  function openMenu(folderId: string) {
    setOpenMenuId((prev) => (prev === folderId ? null : folderId))
  }

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal-backdrop)' as never, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)', width: '100%', maxWidth: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Folders</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 20, padding: 4 }}>×</button>
          </div>

          {/* New folder form */}
          <form onSubmit={handleCreate} style={{ padding: '16px 24px', display: 'flex', gap: 8 }}>
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="New folder name…"
              style={{ flex: 1, padding: '10px 14px', fontSize: 14, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', outline: 'none' }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
            <button
              type="submit"
              disabled={!newTitle.trim() || createFolder.isPending}
              style={{ padding: '10px 18px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: !newTitle.trim() ? 0.5 : 1 }}
            >
              Add
            </button>
          </form>

          {/* Folder list */}
          <div style={{ overflowY: 'auto', padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {folders.length === 0 && (
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', margin: '16px 0' }}>No folders yet</p>
            )}
            {folders.map((folder) => (
              <div
                key={folder.id}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}
              >
                <span style={{ fontSize: 16 }}>📁</span>

                {editingId === folder.id ? (
                  <form onSubmit={handleUpdate} style={{ flex: 1, display: 'flex', gap: 6 }}>
                    <input
                      autoFocus
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      style={{ flex: 1, padding: '6px 10px', fontSize: 14, background: 'var(--bg-surface)', border: '1px solid var(--accent)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', outline: 'none' }}
                    />
                    <button type="submit" disabled={updateFolder.isPending}
                      style={{ padding: '6px 12px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 13, cursor: 'pointer' }}>
                      Save
                    </button>
                    <button type="button" onClick={() => setEditingId(null)}
                      style={{ padding: '6px 12px', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 13, cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </form>
                ) : (
                  <>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{folder.title}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>{folder.deck_count} deck{folder.deck_count !== 1 ? 's' : ''}</span>
                    </div>

                    {/* Rename */}
                    <button onClick={() => startEdit(folder)}
                      style={{ padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
                      Rename
                    </button>

                    {/* ··· overflow menu — course actions */}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <button
                        onClick={() => openMenu(folder.id)}
                        aria-label="Course actions"
                        title="Course actions"
                        style={{ padding: '4px 8px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', minHeight: 30 }}
                      >
                        <MoreHorizontal size={14} />
                      </button>

                      {openMenuId === folder.id && (
                        <FolderCourseMenu
                          onAddToCourse={() => {
                            setOpenMenuId(null)
                            setCourseAction({ type: 'add', folderId: folder.id, folderTitle: folder.title })
                          }}
                          onRemoveFromCourse={() => {
                            setOpenMenuId(null)
                            setCourseAction({ type: 'remove', folderId: folder.id, folderTitle: folder.title })
                          }}
                          onClose={() => setOpenMenuId(null)}
                        />
                      )}
                    </div>

                    {/* Delete */}
                    {confirmDeleteId === folder.id ? (
                      <>
                        <button onClick={() => handleDelete(folder.id)} disabled={deleteFolder.isPending}
                          style={{ padding: '4px 10px', background: 'var(--danger-fill)', color: 'var(--text-on-dark)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
                          Delete
                        </button>
                        <button onClick={() => setConfirmDeleteId(null)}
                          style={{ padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDeleteId(folder.id)}
                        style={{ padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 12, color: 'var(--danger)', cursor: 'pointer', flexShrink: 0 }}>
                        Delete
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Course action sheets rendered outside the modal backdrop so they layer correctly */}
      {courseAction?.type === 'add' && (
        <GroupIntoCourseSheet
          folderIds={[courseAction.folderId]}
          folderTitles={[courseAction.folderTitle]}
          onClose={() => setCourseAction(null)}
          onSuccess={() => setCourseAction(null)}
        />
      )}
      {courseAction?.type === 'remove' && (
        <RemoveFolderFromCourseModal
          folderId={courseAction.folderId}
          folderTitle={courseAction.folderTitle}
          onClose={() => setCourseAction(null)}
          onSuccess={() => setCourseAction(null)}
        />
      )}
    </>
  )
}

// ── Folder course menu (dropdown) ───────────────────────────────────────────────

function FolderCourseMenu({
  onAddToCourse,
  onRemoveFromCourse,
  onClose,
}: {
  onAddToCourse: () => void
  onRemoveFromCourse: () => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)

  // Close on outside click or Escape
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [onClose])

  return (
    <div
      ref={ref}
      role="menu"
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 4px)',
        right: 0,
        zIndex: 300,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-float)',
        minWidth: 190,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '4px 0' }}>
        <button
          role="menuitem"
          onClick={onAddToCourse}
          style={menuItemStyle}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-secondary)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          📚 Add to course
        </button>
        <button
          role="menuitem"
          onClick={onRemoveFromCourse}
          style={menuItemStyle}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-secondary)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          ✕ Remove from course
        </button>
      </div>
    </div>
  )
}

const menuItemStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '9px 14px',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-primary)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  minHeight: 36,
}

// ── Remove folder from course modal ────────────────────────────────────────────
// Shows the user's courses and lets them detach this folder from any of them.
// Removal only severs the course membership — the folder and its decks are untouched.

function RemoveFolderFromCourseModal({
  folderId,
  folderTitle,
  onClose,
  onSuccess,
}: {
  folderId: string
  folderTitle: string
  onClose: () => void
  onSuccess: () => void
}) {
  const { data: coursesData, isLoading } = useCourses(1, 100)
  const removeFolder = useRemoveFolderFromCourse()
  const [confirmCourseId, setConfirmCourseId] = useState<string | null>(null)
  const [removedCourseIds, setRemovedCourseIds] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const allCourses = coursesData?.items ?? []
  // Exclude courses already removed in this session and apply search filter
  const visible = allCourses
    .filter((c) => !removedCourseIds.has(c.id))
    .filter((c) => !search || c.title.toLowerCase().includes(search.toLowerCase()))

  function handleRemove(courseId: string, courseTitle: string) {
    setError(null)
    removeFolder.mutate({ courseId, folderId }, {
      onSuccess: () => {
        setConfirmCourseId(null)
        setRemovedCourseIds((prev) => new Set(prev).add(courseId))
      },
      onError: (err) => {
        setConfirmCourseId(null)
        // 404 means folder wasn't in that course — treat as a no-op, not an error
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 404) {
          setRemovedCourseIds((prev) => new Set(prev).add(courseId))
        } else {
          setError(extractErrorMessage(err, `Could not remove folder from "${courseTitle}". Please try again.`))
        }
      },
    })
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 250, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)', width: '100%', maxWidth: 440, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Remove folder from course</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4, maxWidth: 340 }}>
              Choose a course to detach <strong>"{folderTitle}"</strong> from. The folder and its decks will not be deleted.
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 22, padding: 4, lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>

        {/* Search — only when many courses */}
        {allCourses.length > 5 && (
          <div style={{ padding: '0 24px 12px', flexShrink: 0 }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search courses…"
              style={{ width: '100%', padding: '8px 12px', fontSize: 14, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ margin: '0 24px 12px', padding: '10px 14px', background: 'color-mix(in srgb, var(--danger) 12%, transparent)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-sm)', color: 'var(--danger)', fontSize: 13, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span>{error}</span>
            <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
          </div>
        )}

        {/* Course list */}
        <div style={{ overflowY: 'auto', padding: '0 24px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-secondary)' }} />
            </div>
          ) : visible.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', margin: '20px 0' }}>
              {search
                ? `No courses match "${search}"`
                : removedCourseIds.size > 0
                  ? 'Folder removed from all listed courses.'
                  : 'No courses found.'}
            </p>
          ) : (
            visible.map((course) => (
              <div key={course.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>📚</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{course.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {course.folder_count} folder{course.folder_count !== 1 ? 's' : ''}
                    {course.deck_count > 0 ? ` · ${course.deck_count} deck${course.deck_count !== 1 ? 's' : ''}` : ''}
                  </div>
                </div>
                {confirmCourseId === course.id ? (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      onClick={() => handleRemove(course.id, course.title)}
                      disabled={removeFolder.isPending}
                      style={{ padding: '5px 12px', background: 'var(--danger-fill)', color: 'var(--text-on-dark)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 30 }}
                    >
                      {removeFolder.isPending ? '…' : 'Confirm'}
                    </button>
                    <button
                      onClick={() => setConfirmCourseId(null)}
                      style={{ padding: '5px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 12, cursor: 'pointer', color: 'var(--text-secondary)', minHeight: 30 }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmCourseId(course.id)}
                    style={{ padding: '5px 12px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 12, cursor: 'pointer', color: 'var(--text-secondary)', flexShrink: 0, minHeight: 34 }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--danger)'; e.currentTarget.style.color = 'var(--danger)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)' }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer — done button */}
        <div style={{ padding: '12px 24px 20px', flexShrink: 0, borderTop: '1px solid var(--border)' }}>
          <button
            onClick={() => { if (removedCourseIds.size > 0) onSuccess(); onClose() }}
            style={{ width: '100%', padding: '11px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            {removedCourseIds.size > 0 ? `Done (${removedCourseIds.size} removed)` : 'Done'}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
